import { Link } from 'react-router-dom'
import { SCHEDULES_PATH } from '@config/reportRoutes'

/**
 * The Queue page's entry point to Recurring schedules (spec 074).
 *
 * It carries a count and a dormant-schedule warning rather than being a bare
 * button, for two reasons learned from a live report that "a schedule I created
 * doesn't appear":
 *
 *  1. A new schedule produces NO queue row until it first fires — up to a week
 *     later for a weekly one — so the Queue page looked empty right after
 *     creating one, with nothing to say a schedule existed at all.
 *  2. `needsSignIn` (the honest "this schedule has gone dormant" state that the
 *     whole credential design exists to surface) was rendered ONLY on the
 *     schedules page, one click behind this control. A state the owner must act
 *     on should not be hidden behind a click.
 *
 * ## Two different things are both called "paused" — keep them apart (CC-50272)
 *
 * A schedule can be stopped in two unrelated ways, and `ScheduleCard` already
 * distinguishes them:
 *
 *   * `enabled === false` — the OWNER turned Active off. `ScheduleCard` renders an
 *     uppercase amber **PAUSED** chip (`ScheduleCard.jsx:158-166`).
 *   * `needsSignIn === true` — the stored credential is gone, so it cannot fire.
 *     `ScheduleCard` says "Paused automatically — sign in again to restart it."
 *     (`ScheduleCard.jsx:206-210`).
 *
 * This control used to render one chip reading "N paused" while counting only the
 * SECOND. QA paused a schedule, saw PAUSED on the schedules page, and got no chip
 * here at all — reported as the chip being broken, and it was: the word and the
 * number referred to different states. (Live on QA at the time: five schedules,
 * two with `enabled:false`, zero with `needsSignIn` — so the chip was correctly
 * rendering nothing for a count that was genuinely 0, while the page next door
 * showed two PAUSED rows.)
 *
 * So both are counted and each says which it is. `paused` keeps amber to match the
 * chip on the schedules page — a reader must not have to learn two colour codes for
 * one word. `needsSignIn` gets the stronger red-amber and comes SECOND in the
 * label, because it is the one the owner has to act on; owner-paused is a state
 * they chose.
 */
const ManageSchedulesLink = ({ total = 0, paused = 0, needsSignIn = 0 }) => {
  // "N active" would be wrong: `total` counts paused schedules too, so a firm with
  // two live and one paused schedule was told it had "3 active". On a page whose
  // job is saying what will actually run, that is the wrong direction to be wrong in.
  const parts = []
  if (total > 0) parts.push(`${total} total`)
  if (paused > 0) parts.push(`${paused} paused`)
  // Each part must be the VISIBLE chip's words, not a synonym of them. This one read
  // "needing sign-in" while the chip 18 lines down says "needs sign-in", which is a
  // WCAG 2.5.3 Label-in-Name failure and not merely drift: `aria-label` REPLACES the
  // accessible name, so a speech-input user saying what they can see ("needs sign-in")
  // addressed a control whose name did not contain it. The link text itself
  // ("Manage schedules") is deliberately untouched.
  if (needsSignIn > 0) parts.push(`${needsSignIn} needs sign-in`)
  const label = parts.length ? `Manage schedules — ${parts.join(', ')}` : 'Manage schedules'

  return (
    <Link to={SCHEDULES_PATH} className="btn-cb-toolbar" aria-label={label}>
      Manage schedules
      {total > 0 && (
        <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 text-[10.5px] font-semibold text-gray-600">
          {total}
        </span>
      )}
      {paused > 0 && (
        <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 text-[10.5px] font-semibold text-amber-800">
          {paused} paused
        </span>
      )}
      {needsSignIn > 0 && (
        <span className="ml-1.5 rounded-full bg-red-100 px-1.5 text-[10.5px] font-semibold text-red-800">
          {needsSignIn} needs sign-in
        </span>
      )}
    </Link>
  )
}

export default ManageSchedulesLink
